/**
 * Waterfall (cumulative spectral decay) — cascade of receding decay-vs-
 * frequency polylines, age-tinted so the newest (front) trace reads clearly
 * over the older ones behind it. Paint layer only — see `../waterfall.ts`
 * for the cascade reshape math (computeWaterfallGeometry).
 *
 * Mirrors web's `web/src/components/room/charts/waterfallDraw.ts`
 * `drawWaterfall`, ported to mobile's light ink convention (see `./_shared`)
 * instead of web's dark dock theme — same geometry, different paint.
 */

import { canvasTypeFont } from '@roomtreatment/design';
import { setupCanvas, drawEmptyMessage, INK, withAlpha } from './_shared';
import { computeWaterfallGeometry } from '../waterfall';
import { formatHz } from '../ticks';
import type { ChartPad } from '../layout';
import type { RT60DecayCurve } from '../registry';

export const WATERFALL_PAD: ChartPad = { t: 10, r: 14, b: 22, l: 14 };

export interface DrawWaterfallOpts {
  decay: RT60DecayCurve;
  width: number;
  height: number;
  maxTimeS?: number;
  sliceCount?: number;
}

export function drawWaterfallCanvas(canvas: HTMLCanvasElement, opts: DrawWaterfallOpts): void {
  const s = setupCanvas(canvas, opts.width, opts.height);
  if (!s) return;
  const { ctx, W, H } = s;

  const geo = computeWaterfallGeometry({
    decay: opts.decay,
    width: opts.width,
    height: opts.height,
    pad: WATERFALL_PAD,
    maxTimeS: opts.maxTimeS,
    sliceCount: opts.sliceCount,
  });

  if (geo.empty) {
    drawEmptyMessage(ctx, W, H, 'No decay curve yet — ≥200 Hz only');
    return;
  }

  const { frame } = geo;
  // Oldest slice first so newer (louder / more relevant) slices paint on
  // top — load-bearing ordering, matches web's waterfallDraw.ts exactly.
  const ordered = [...geo.slices].reverse();
  for (const slice of ordered) {
    if (slice.points.length < 2) continue;
    const opacity = 0.15 + (1 - slice.age) * 0.75;
    ctx.strokeStyle = withAlpha(INK.primary, opacity);
    ctx.lineWidth = slice.age === 0 ? 1.8 : 1.1;
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(frame.plotX + slice.points[0]!.x, frame.plotY + slice.points[0]!.y);
    for (let i = 1; i < slice.points.length; i++) {
      ctx.lineTo(frame.plotX + slice.points[i]!.x, frame.plotY + slice.points[i]!.y);
    }
    ctx.stroke();
  }

  ctx.font = canvasTypeFont('tick');
  ctx.textAlign = 'center';
  ctx.fillStyle = INK.label;
  const shown = geo.freqLabels.filter((_, i) => i % Math.ceil(geo.freqLabels.length / 6 || 1) === 0);
  for (const fl of shown) {
    ctx.fillText(formatHz(fl.hz), frame.plotX + fl.x, frame.plotY + frame.plotH + 12);
  }
}
